import { byMethod } from '@oysterjs/core/graphql/actions';
import { merge } from '@oysterjs/core/graphql/loaders';
import type { RouteObject } from 'react-router-dom';
import {
  submitArchiveEmailTemplate,
  submitCreateEmailTemplate,
  submitDuplicateEmailTemplate,
  submitUpdateEmailTemplate
} from '~/api/data/email-templates/mutations';
import { loadEmailTemplate, loadEmailTemplates } from '~/api/data/email-templates/queries';
import { Action, Resource } from '~/api/graphql';
import { FEATURE_FLAGS } from '~/components/feature-flags';
import { FlagGuard } from '~/components/flag-guard';
import { LibraryListLoadError } from '~/components/list/library-list-load-error';
import { ProtectedRoute } from '~/components/permissions';
import { EMAIL_TEMPLATES_PATH } from '~/components/email-templates/email-template-paths';
import {
  EmailTemplateEditorLoadError,
  EmailTemplateEditorPage
} from '~/pages/library/email-template';
import { LibraryEmailTemplatesPage } from '~/pages/library/email-templates';

const LIBRARY_PATH = '/app/library';

export const emailTemplatesRoute: RouteObject = {
  element: <FlagGuard flag={FEATURE_FLAGS.EMAIL_TEMPLATES} to={LIBRARY_PATH} />,
  children: [
    {
      path: 'email-templates',
      loader: merge({ emailTemplates: loadEmailTemplates }),
      element: (
        <ProtectedRoute
          permissions={[{ resource: Resource.EmailTemplate, action: Action.List }]}
          redirectTo={LIBRARY_PATH}
        >
          <LibraryEmailTemplatesPage />
        </ProtectedRoute>
      ),
      errorElement: <LibraryListLoadError title="We couldn't load your email templates." />,
      children: [
        { path: ':templateId/duplicate', action: byMethod({ POST: submitDuplicateEmailTemplate }) },
        { path: ':templateId/archive', action: byMethod({ POST: submitArchiveEmailTemplate }) }
      ]
    }
  ]
};

/** Outside the Library page, since the editor titles itself and its preview needs the width. */
export const emailTemplateEditorRoute: RouteObject = {
  element: <FlagGuard flag={FEATURE_FLAGS.EMAIL_TEMPLATES} to={LIBRARY_PATH} />,
  children: [
    {
      path: 'library/email-templates/new',
      action: byMethod({ POST: submitCreateEmailTemplate }),
      element: (
        <ProtectedRoute
          permissions={[{ resource: Resource.EmailTemplate, action: Action.Create }]}
          redirectTo={EMAIL_TEMPLATES_PATH}
        >
          <EmailTemplateEditorPage />
        </ProtectedRoute>
      ),
      errorElement: <EmailTemplateEditorLoadError />
    },
    {
      path: 'library/email-templates/:templateId',
      loader: merge({ emailTemplate: loadEmailTemplate }),
      action: byMethod({ PUT: submitUpdateEmailTemplate }),
      element: (
        <ProtectedRoute
          permissions={[{ resource: Resource.EmailTemplate, action: Action.Read }]}
          redirectTo={EMAIL_TEMPLATES_PATH}
        >
          <EmailTemplateEditorPage />
        </ProtectedRoute>
      ),
      errorElement: <EmailTemplateEditorLoadError />
    }
  ]
};
